/**
 * iOS / Safari unlock helper.
 *
 * WebKit suspends every freshly-created `AudioContext` until it observes
 * a user gesture. Calling `ctx.resume()` outside that gesture window
 * silently fails — and any subsequent `OscillatorNode.start()` will be
 * scheduled but never make a sound.
 *
 * The pattern below is the canonical fix:
 *   1. Resume the context (no-op on Chrome/Firefox where it's already
 *      "running").
 *   2. Play a one-sample silent buffer through the destination. Some
 *      iOS revisions need an actual scheduled play to flip the audio
 *      hardware on, even after `resume()` reports success.
 *
 * Must be invoked from inside a user-gesture handler (touchstart, click,
 * keydown, pointerdown). Calling on page load alone is not enough.
 */
export async function unlockAudioContext(ctx: AudioContext): Promise<void> {
  if (ctx.state === "suspended") {
    try {
      await ctx.resume();
    } catch (err) {
      console.warn("[VuAudio] Context resume failed:", err);
    }
  }

  if (ctx.state === "running") {
    const buffer = ctx.createBuffer(1, 1, 22050);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    source.start(0);
  }
}
